import { Component, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { UserService } from 'src/app/services/user.service';
import { UserModalComponent } from '../user-modal/user-modal.component';
import { ConfirmationModalComponent } from 'src/app/confirmation-modal/confirmation-modal.component';
import { Constants } from 'src/app/shared/constants';
import { FormUser, User } from 'src/app/models/user.interface';

@Component({
  selector: 'app-manage-users',
  templateUrl: './manage-users.component.html',
  styleUrls: ['./manage-users.component.scss']
})
export class ManageUsersComponent implements OnInit {
  displayedColumns: string[] = ['provider', 'email', 'isAdmin', 'actions'];

  users: FormUser[] = [];
  userEmail = '';
  isAdmin = false;
  loadingUsers = false;
  userActionInProgress = false;
  usersErrorMessage = '';
  usersDataSource = new MatTableDataSource<FormUser>([]);
  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private userService: UserService
  ) {}

  ngOnInit(): void {
    this.fetchUsers();
  }

  openAddUserModal(): void {
    const dialogRef = this.dialog.open(UserModalComponent, {
      width: '400px',
      data: { email: '', isAdmin: false }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.addUser(result.email, result.isAdmin);
      }
    });
  }

  addUser(email: string, isAdmin: boolean) {
    this.userActionInProgress = true;
    this.userService.addOrUpdateUser(email, isAdmin).subscribe({
      next: () => {
        this.userActionInProgress = false;
        this.fetchUsers();
        this.snackBar.open('User added successfully', 'Close', {
          duration: 3000
        });
      },
      error: error => {
        this.userActionInProgress = false;
        console.error('Error adding user:', error);
        this.snackBar.open('Error adding user', 'Close', { duration: 3000 });
      }
    });
  }

  toggleAdminStatus(user: User) {
    this.userActionInProgress = true;
    const userEmail = user.email;
    this.userService.addOrUpdateUser(userEmail, !user.isAdmin).subscribe({
      next: () => {
        this.userActionInProgress = false;
        this.fetchUsers();
        this.snackBar.open(`User admin status ${user.isAdmin ? 'revoked' : 'granted'}`, 'Close', { duration: 3000 });
      },
      error: error => {
        this.userActionInProgress = false;
        console.error('Error toggling admin status:', error);
        this.snackBar.open('Error toggling admin status', 'Close', {
          duration: 3000
        });
      }
    });
  }

  confirmDeleteUser(email: string) {
    const dialogRef = this.dialog.open(ConfirmationModalComponent, {
      data: { message: `Are you sure you want to delete ${email}?` }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.deleteUser(email);
      }
    });
  }

  deleteUser(email: string) {
    this.userActionInProgress = true;
    this.userService.deleteUser(email).subscribe({
      next: () => {
        this.userActionInProgress = false;
        this.fetchUsers();
        this.snackBar.open('User deleted successfully', 'Close', {
          duration: 3000
        });
      },
      error: error => {
        this.userActionInProgress = false;
        console.error('Error deleting user:', error);
        this.snackBar.open('Error deleting user', 'Close', { duration: 3000 });
      }
    });
  }

  // Add this method to filter users based on input
  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.usersDataSource.filter = filterValue.trim().toLowerCase();
  }

  parseProviderNameIfDefined(user: User) {
    const userIdentities = user.Attributes?.find(
      (attr: { Name: string; Value: string }) => attr.Name === 'identities'
    )?.['Value'];
    if (userIdentities) {
      try {
        const providerName = JSON.parse(userIdentities)[0]?.providerName;
        if (providerName) {
          return Constants.SSO_PROVIDER_DISPLAY_MAP[providerName] ?? providerName;
        }
      } catch (error) {
        console.error('Error parsing JSON:', error);
      }
    }
    return '';
  }

  // Update the fetchUsers method to use MatTableDataSource
  fetchUsers() {
    this.loadingUsers = true;
    this.usersErrorMessage = '';

    this.userService.getUsers().subscribe({
      next: (data: User[]) => {
        console.log(data);
        this.users = data.map(
          (user: User): FormUser => ({
            user_id: user.CognitoUserId ?? '',
            provider: this.parseProviderNameIfDefined(user),
            email: user.Attributes?.find(attr => attr.Name === 'email')?.Value ?? user.Username ?? '',
            isAdmin: user.Attributes?.find(attr => attr.Name === 'custom:isAdmin')?.Value === 'true'
          })
        );
        this.usersDataSource.data = this.users.sort((a: FormUser, b: FormUser) =>
          (a.provider + a.email).localeCompare(b.provider + b.email)
        );
        this.loadingUsers = false;
      },
      error: error => {
        console.error('Error fetching users:', error);
        this.usersErrorMessage = 'Error fetching users. Please try again.';
        this.loadingUsers = false;
      }
    });
  }
}
